"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import toast from "react-hot-toast";
import {
  ArrowRight,
  Loader2,
  Smartphone,
  Wifi,
  X,
} from "lucide-react";

import { getToken } from "@/services/authStorage";
import {
  cancelDgiConsulta,
  cancelDgiPortalUserAutomation,
  getDgiConsultaSession,
  getDgiConsultaSessionWsUrl,
  getDgiPortalUserAutomationSession,
  startDgiConsulta,
  startDgiPortalUserAutomation,
  startOnboardingSsoRefresh,
  submitDgiConsultaStep,
  submitDgiPortalUserAutomationStep,
} from "@/services/fiscalService";

import { OnboardingChoiceCard } from "@/components/onboarding/OnboardingChoiceCard";
import { translateDgiConsultaLastError } from "@/lib/dgiConsultaLastError";
import { cn } from "@/lib/utils";
import { DgiDocumentReadyCard } from "@/pages/dgi-documents/dgi-document-ready-card";

const POLL_MS = 3000;
const LOADING_SLOW_AFTER_MS = 30_000;

/**
 * Survives React Strict Mode remounts (refs reset per mount). Used to ignore stale
 * startDgiConsulta completions when the effect re-runs and a newer run is active.
 */
let consultaModalStartNonce = 0;

export interface DgiConsultaFlowModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Ignored when variant is portal-user/sso-refresh (automation uses server-side context). */
  documentKey: string;
  documentTitle: string;
  /**
   * consulta = Ultra document PDF;
   * portal-user = onboarding e-Factura solicitud automation;
   * sso-refresh = re-authenticate to refresh expired SSO storage state (used to
   * recover a failed onboarding sub-step).
   */
  variant?: "consulta" | "portal-user" | "sso-refresh";
  onAutomationUnavailable?: () => void;
  /**
   * Optional: e.g. analytics when done (PDF for consulta, or portal-user form submitted).
   * For variant="portal-user", `sessionId` is included so the caller can continue
   * polling the session for post-SSO progress (the modal closes early after auth).
   */
  onFlowSuccess?: (payload: {
    resultPdfUrl?: string;
    documentKey: string;
    sessionId?: string;
  }) => void;
  /** Portal-user only: automation finished with status failed (once per run). */
  onPortalAutomationFailed?: () => void;
}

type Phase = "idle" | "starting" | "interactive" | "failed" | "success";

export function DgiConsultaFlowModal({
  open,
  onOpenChange,
  documentKey,
  documentTitle,
  variant = "consulta",
  onAutomationUnavailable,
  onFlowSuccess,
  onPortalAutomationFailed,
}: DgiConsultaFlowModalProps): JSX.Element {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>("idle");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [status, setStatus] = useState<string | undefined>();
  const [currentStep, setCurrentStep] = useState<string | undefined>();
  const [lastError, setLastError] = useState<string | undefined>();
  const [resultPdfUrl, setResultPdfUrl] = useState<string | null>(null);

  const [abitabDocType, setAbitabDocType] = useState<"cedula" | "pasaporte">("cedula");
  const [abitabDocNumber, setAbitabDocNumber] = useState("");
  const [tuidDocument, setTuidDocument] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showSlowLoadingHint, setShowSlowLoadingHint] = useState(false);
  /** When true and `lastError` is empty, failure UI shows "cancelled" instead of generic failed. */
  const [failureIsCancelled, setFailureIsCancelled] = useState(false);

  const wsActiveRef = useRef(false);
  const cancelledRef = useRef(false);
  const successToastSentRef = useRef(false);
  const flowSuccessHandledRef = useRef(false);
  const portalUserFailureNotifiedRef = useRef(false);
  /** Snapshot when user submits a step; cleared when automation advances `status` or `currentStep`. */
  const pendingSubmitAnchorRef = useRef<{
    status: string | undefined;
    currentStep: string | undefined;
  } | null>(null);

  const resetState = useCallback((): void => {
    setPhase("idle");
    setSessionId(null);
    setStatus(undefined);
    setCurrentStep(undefined);
    setLastError(undefined);
    setResultPdfUrl(null);
    setAbitabDocType("cedula");
    setAbitabDocNumber("");
    setTuidDocument("");
    setSubmitting(false);
    setShowSlowLoadingHint(false);
    setFailureIsCancelled(false);
    pendingSubmitAnchorRef.current = null;
    successToastSentRef.current = false;
    flowSuccessHandledRef.current = false;
    portalUserFailureNotifiedRef.current = false;
  }, []);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }
    cancelledRef.current = false;
    const startNonce = ++consultaModalStartNonce;
    const abortController = new AbortController();

    const run = async (): Promise<void> => {
      setPhase("starting");
      try {
        let sid: string;
        if (variant === "portal-user") {
          ({ sessionId: sid } = await startDgiPortalUserAutomation({
            signal: abortController.signal,
          }));
        } else if (variant === "sso-refresh") {
          const r = await startOnboardingSsoRefresh();
          if (!r.accepted) throw new Error(r.message ?? "SSO refresh unavailable");
          sid = r.sessionId;
        } else {
          ({ sessionId: sid } = await startDgiConsulta(documentKey, {
            signal: abortController.signal,
          }));
        }
        if (cancelledRef.current || startNonce !== consultaModalStartNonce) return;
        setSessionId(sid);
        setPhase("interactive");
      } catch (err) {
        if (cancelledRef.current || startNonce !== consultaModalStartNonce) return;
        if (axios.isCancel(err)) return;
        toast.error(
          variant === "consulta"
            ? t("dgiDocuments.modal.automationUnavailable")
            : t("onboarding.dgiPortalAutomationUnavailable"),
          {
            id:
              variant === "consulta"
                ? "dgi-consulta-automation-unavailable"
                : "dgi-portal-user-automation-unavailable",
          }
        );
        onAutomationUnavailable?.();
        setPhase("failed");
      }
    };

    void run();
    return () => {
      cancelledRef.current = true;
      abortController.abort();
    };
  }, [open, documentKey, variant, onAutomationUnavailable, resetState, t]);

  const downloadFilename = useMemo((): string => {
    const safe = documentKey.replace(/[^a-z0-9-]/gi, "-");
    return `dgi-${safe}.pdf`;
  }, [documentKey]);

  const applySession = useCallback(
    (s: {
      status?: string;
      currentStep?: string;
      resultPdfUrl?: string;
      lastError?: string;
    }): void => {
      const anchor = pendingSubmitAnchorRef.current;
      if (anchor !== null) {
        const nextStatus = s.status;
        const nextStep = s.currentStep;
        const statusDiff = (nextStatus ?? "") !== (anchor.status ?? "");
        const stepDiff = (nextStep ?? "") !== (anchor.currentStep ?? "");
        if (statusDiff || stepDiff) {
          pendingSubmitAnchorRef.current = null;
          setSubmitting(false);
        }
      }
      setStatus(s.status);
      setCurrentStep(s.currentStep);
      if (s.status === "cancelled") {
        pendingSubmitAnchorRef.current = null;
        setSubmitting(false);
        setFailureIsCancelled(true);
        setPhase("failed");
        const rawCancel = (s.lastError ?? "").trim();
        setLastError(rawCancel || undefined);
        const msg = rawCancel
          ? translateDgiConsultaLastError(rawCancel, t)
          : t("dgiDocuments.modal.cancelled");
        toast.error(msg, {
          id:
            variant === "portal-user"
              ? "dgi-portal-user-cancelled"
              : "dgi-consulta-cancelled",
        });
        return;
      }
      const isAutomationOnly = variant !== "consulta";
      // Early close for portal-user: the modal exists only to drive the
      // push-notification SSO. The instant the Python flow signals that auth
      // succeeded (any post-push checkpoint), hand the session off to the
      // auto-flow hook and close. The remaining work (entity selection, aviso
      // click, e-Factura form fill, delete-and-recreate flow) happens inline.
      const POST_AUTH_CURRENT_STEPS = new Set([
        // Emitted by run_interactive_sso_identidades_to_servicios_ready right
        // after push approval (Abitab/TuID returns to ID Uruguay):
        "post_push_consent",
        "dgi_redirect",
        // Emitted after entity-selection + Aviso al Contribuyente clear:
        "efactura_solicitud_form",
        "efactura_open_form",
        "efactura_resume_session",
        // Fallback for the no-SSO resume path:
        "efactura_regenerating_user",
      ]);
      if (
        variant === "portal-user" &&
        s.status === "running" &&
        s.currentStep &&
        POST_AUTH_CURRENT_STEPS.has(s.currentStep) &&
        !flowSuccessHandledRef.current
      ) {
        flowSuccessHandledRef.current = true;
        onFlowSuccess?.({
          documentKey: "efactura-portal-user",
          sessionId: sessionId ?? undefined,
        });
        onOpenChange(false);
        return;
      }
      if (
        s.status === "success" &&
        (s.resultPdfUrl || isAutomationOnly)
      ) {
        setLastError(undefined);
        if (isAutomationOnly) {
          if (!flowSuccessHandledRef.current) {
            flowSuccessHandledRef.current = true;
            onFlowSuccess?.({
              resultPdfUrl: s.resultPdfUrl,
              documentKey:
                variant === "sso-refresh"
                  ? "sso-refresh"
                  : "efactura-portal-user",
              sessionId: sessionId ?? undefined,
            });
            onOpenChange(false);
          }
          return;
        }
        if (!successToastSentRef.current) {
          successToastSentRef.current = true;
          toast.success(t("dgiDocuments.modal.success"));
        }
        if (!flowSuccessHandledRef.current) {
          flowSuccessHandledRef.current = true;
          setResultPdfUrl(s.resultPdfUrl ?? null);
          setPhase("success");
          onFlowSuccess?.({
            resultPdfUrl: s.resultPdfUrl,
            documentKey,
          });
        }
        return;
      }
      if (s.status === "failed") {
        pendingSubmitAnchorRef.current = null;
        setSubmitting(false);
        setFailureIsCancelled(false);
        const rawFail = (s.lastError ?? "").trim();
        setLastError(rawFail || undefined);
        setPhase("failed");
        if (variant !== "consulta" && !portalUserFailureNotifiedRef.current) {
          portalUserFailureNotifiedRef.current = true;
          onPortalAutomationFailed?.();
        }
        toast.error(translateDgiConsultaLastError(rawFail || undefined, t), {
          id:
            variant === "portal-user"
              ? "dgi-portal-user-failed"
              : variant === "sso-refresh"
                ? "dgi-sso-refresh-failed"
                : "dgi-consulta-failed",
        });
        return;
      }
      if (s.lastError) setLastError(s.lastError);
    },
    [documentKey, onFlowSuccess, onOpenChange, onPortalAutomationFailed, sessionId, t, variant]
  );

  /**
   * Session updates: WebSocket to the API (`/ws/dgi-consulta-session`), same pattern as boleto.
   * The Python automation only talks to the backend over HTTP (webhooks); it never exposes WS to the portal.
   */
  useEffect(() => {
    if (!open || !sessionId || phase !== "interactive") return;

    const poll = async (): Promise<void> => {
      if (cancelledRef.current) return;
      // WS push is the fast path, but we keep polling running as a backstop so
      // a silently-broken WS (auth edge cases, subscribe race, dropped frame)
      // can't strand the modal on "Preparando el ingreso…" forever. The
      // duplicate requests are cheap compared to a stuck flow.
      try {
        const s =
          variant === "portal-user"
            ? await getDgiPortalUserAutomationSession(sessionId)
            : await getDgiConsultaSession(sessionId); // sso-refresh + consulta both live in consulta store
        if (cancelledRef.current) return;
        applySession(s);
      } catch {
        /* network */
      }
    };

    const interval = setInterval(poll, POLL_MS);
    void poll();
    return () => clearInterval(interval);
  }, [open, sessionId, phase, applySession, variant]);

  useEffect(() => {
    if (!open || !sessionId || phase !== "interactive") return;
    const token = getToken();
    if (!token) return;

    const wsUrl = `${getDgiConsultaSessionWsUrl()}?token=${encodeURIComponent(token)}`;
    let ws: WebSocket | null = null;
    const onCloseOrError = (): void => {
      wsActiveRef.current = false;
    };
    try {
      ws = new WebSocket(wsUrl);
      ws.onopen = (): void => {
        wsActiveRef.current = true;
        setTimeout(() => {
          ws?.send(JSON.stringify({ event: "subscribe", data: { sessionId } }));
        }, 200);
      };
      ws.onmessage = (ev: MessageEvent): void => {
        try {
          const msg = JSON.parse(ev.data as string) as {
            event?: string;
            data?: {
              status?: string;
              currentStep?: string;
              resultPdfUrl?: string;
              lastError?: string;
            };
          };
          if (msg.event !== "session" || !msg.data) return;
          applySession(msg.data);
        } catch {
          /* ignore */
        }
      };
      ws.onclose = onCloseOrError;
      ws.onerror = onCloseOrError;
    } catch {
      wsActiveRef.current = false;
    }
    return () => {
      wsActiveRef.current = false;
      ws?.close();
    };
  }, [open, sessionId, phase, applySession, variant]);

  const submitStep = async (
    payload: Parameters<typeof submitDgiConsultaStep>[0]
  ): Promise<void> => {
    if (variant === "portal-user") {
      await submitDgiPortalUserAutomationStep(payload);
    } else {
      await submitDgiConsultaStep(payload);
    }
  };

  const handleSubmitIdp = async (choice: "abitab" | "tuid"): Promise<void> => {
    if (!sessionId) return;
    pendingSubmitAnchorRef.current = { status, currentStep };
    setSubmitting(true);
    try {
      await submitStep({ sessionId, idpChoice: choice });
    } catch {
      pendingSubmitAnchorRef.current = null;
      setSubmitting(false);
      toast.error(t("dgiDocuments.modal.failed"));
    }
  };

  const handleSubmitAbitab = async (): Promise<void> => {
    if (!sessionId || !abitabDocNumber.trim()) return;
    pendingSubmitAnchorRef.current = { status, currentStep };
    setSubmitting(true);
    try {
      await submitStep({
        sessionId,
        abitabDocumentType: abitabDocType,
        abitabDocumentNumber: abitabDocNumber.trim(),
      });
    } catch {
      pendingSubmitAnchorRef.current = null;
      setSubmitting(false);
      toast.error(t("dgiDocuments.modal.failed"));
    }
  };

  const handleSubmitTuidApp = async (): Promise<void> => {
    if (!sessionId) return;
    pendingSubmitAnchorRef.current = { status, currentStep };
    setSubmitting(true);
    try {
      await submitStep({ sessionId, tuidAppMobileConfirmed: true });
    } catch {
      pendingSubmitAnchorRef.current = null;
      setSubmitting(false);
      toast.error(t("dgiDocuments.modal.failed"));
    }
  };

  const handleSubmitTuidDoc = async (): Promise<void> => {
    if (!sessionId || !tuidDocument.trim()) return;
    pendingSubmitAnchorRef.current = { status, currentStep };
    setSubmitting(true);
    try {
      await submitStep({
        sessionId,
        tuidDocument: tuidDocument.trim(),
      });
    } catch {
      pendingSubmitAnchorRef.current = null;
      setSubmitting(false);
      toast.error(t("dgiDocuments.modal.failed"));
    }
  };

  const cancelAutomationIfActive = useCallback(async (): Promise<void> => {
    if (!sessionId) return;
    if (phase === "idle" || phase === "failed" || phase === "success") return;
    try {
      if (variant === "portal-user") {
        await cancelDgiPortalUserAutomation(sessionId);
      } else {
        await cancelDgiConsulta(sessionId);
      }
    } catch {
      /* ignore */
    }
  }, [sessionId, phase, variant]);

  const requestClose = useCallback((): void => {
    if (phase === "starting") return;
    if (phase === "success") {
      onOpenChange(false);
      return;
    }
    void cancelAutomationIfActive().finally(() => onOpenChange(false));
  }, [phase, cancelAutomationIfActive, onOpenChange]);

  const handleClose = (): void => {
    requestClose();
  };

  const showDocumentLoading =
    status === "running" ||
    currentStep === "open_document_page" ||
    (currentStep ?? "").startsWith("efactura");
  const showPushWait = status === "awaiting_push" && !showDocumentLoading;
  const showIdp = status === "awaiting_idp";
  const showAbitab = status === "awaiting_abitab_document";
  const showTuidMethod = status === "awaiting_tuid_method";
  const showTuidDoc = status === "awaiting_tuid_identifier";

  const hasInteractivePanel =
    showIdp || showAbitab || showTuidMethod || showTuidDoc;

  /** Covers session `starting` (before first webhook), push approval, and PDF capture. */
  const blocking =
    phase === "starting" ||
    (phase === "interactive" &&
      !hasInteractivePanel &&
      (showPushWait ||
        showDocumentLoading ||
        status === "starting" ||
        status === undefined ||
        status === ""));

  /** Keep overlay until automation advances session (not only until POST returns). */
  const submitLoading = submitting && phase === "interactive";

  const showLoadingOverlay = Boolean(blocking || submitLoading);

  useEffect(() => {
    if (!open) {
      setShowSlowLoadingHint(false);
      return;
    }
    if (!showLoadingOverlay) {
      setShowSlowLoadingHint(false);
      return;
    }
    setShowSlowLoadingHint(false);
    const timerId = window.setTimeout(() => {
      setShowSlowLoadingHint(true);
    }, LOADING_SLOW_AFTER_MS);
    return () => window.clearTimeout(timerId);
  }, [open, showLoadingOverlay]);

  const isEfacturaAutomationStep = (currentStep ?? "").startsWith("efactura");

  const isAutomationOnly = variant !== "consulta";

  const handleDialogOpenChange = (nextOpen: boolean): void => {
    if (!nextOpen && phase === "starting") return;
    if (!nextOpen) {
      requestClose();
      return;
    }
    onOpenChange(nextOpen);
  };

  const handleSuccessDismiss = useCallback((): void => {
    onOpenChange(false);
  }, [onOpenChange]);

  if (!open) return <></>;

  if (phase === "success" && resultPdfUrl) {
    // Preserve the original success card for the consulta variant (Ultra
    // documents). Portal-user variant closes itself elsewhere so it doesn't
    // hit this branch.
    return createPortal(
      <div className="fx-onb-modal-backdrop" role="dialog" aria-modal="true">
        <div className="fx-onb-modal compact">
          <div className="fx-onb-modal-form">
            <DgiDocumentReadyCard
              title={documentTitle}
              description={t("dgiDocuments.readyCard.description")}
              downloadLabel={t("dgiDocuments.readyCard.downloadPdf")}
              dismissLabel={t("dgiDocuments.readyCard.dismiss")}
              downloadErrorMessage={t("dgiDocuments.readyCard.downloadError")}
              pdfUrl={resultPdfUrl}
              downloadFilename={downloadFilename}
              onDismiss={handleSuccessDismiss}
            />
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  const headingAccent =
    isAutomationOnly ? "DGI." : documentTitle.split(" ").pop() ?? "";
  const headingLead = isAutomationOnly
    ? variant === "sso-refresh"
      ? t("onboarding.dgiAccessStep.reauthModalH2Lead", "Volver a autenticarte con")
      : t("onboarding.dgiAccessStep.authModalH2Lead", "Autenticación con")
    : documentTitle.replace(headingAccent, "").trim();
  const subtitle = isAutomationOnly
    ? variant === "sso-refresh"
      ? t(
          "onboarding.dgiAccessStep.reauthModalSubtitle",
          "Tu sesión con DGI expiró. Reautenticate con Abitab o TuID para que sigamos.",
        )
      : t(
          "onboarding.dgiPortalAutomationModalSubtitle",
          "Elegí tu método habitual: ID Abitab o TuID — Antel.",
        )
    : t("dgiDocuments.modal.subtitle");

  return createPortal(
    <div
      className="fx-onb-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={documentTitle}
    >
      <div className="fx-onb-modal compact">
        <div className="fx-onb-modal-form">
          <button
            type="button"
            className="fx-onb-modal-close"
            onClick={handleClose}
            disabled={phase === "starting"}
            aria-label={t("common.close", "Cerrar")}
          >
            <X aria-hidden />
          </button>

          <span className="fx-onb-modal-eyebrow">
            {isAutomationOnly
              ? variant === "sso-refresh"
                ? t(
                    "onboarding.dgiAccessStep.reauthModalEyebrow",
                    "Reautenticación",
                  )
                : t("onboarding.dgiAccessStep.authModalEyebrow", "Autenticación")
              : t("dgiDocuments.modal.eyebrow", "Documento DGI")}
          </span>
          <h2 className="fx-onb-modal-h2">
            {headingLead}{" "}
            <span className="ital">{headingAccent}</span>
          </h2>
          <p className="fx-onb-modal-lead">{subtitle}</p>

          {phase === "starting" ||
          (phase === "interactive" && blocking) ||
          submitting ? (
            <div className="fx-onb-push-hero">
              <span className="ring" aria-hidden>
                {showPushWait ? <Smartphone /> : <Wifi />}
              </span>
              <p className="title">
                {showPushWait ? (
                  <>
                    {t(
                      "dgiDocuments.modal.pushHeroTitleLead",
                      "Aprobá el ingreso en tu",
                    )}{" "}
                    <span className="ital">
                      {t("dgiDocuments.modal.pushHeroTitleAccent", "teléfono.")}
                    </span>
                  </>
                ) : (
                  <>
                    {t(
                      "dgiDocuments.modal.connectingTitleLead",
                      "Conectando con",
                    )}{" "}
                    <span className="ital">
                      {t(
                        "dgiDocuments.modal.connectingTitleAccent",
                        "DGI.",
                      )}
                    </span>
                  </>
                )}
              </p>
              {currentStep === "efactura_regenerating_user" ? (
                <p className="sub">
                  {t(
                    "dgiDocuments.modal.regeneratingUser",
                    "Encontramos un usuario antiguo en DGI — lo estamos regenerando con tus datos actuales…",
                  )}
                </p>
              ) : showPushWait ? (
                <p className="sub">
                  {t(
                    "dgiDocuments.modal.pushHeroSub",
                    "Abrí la app de Abitab o TuID en tu teléfono y aprobá la solicitud ahora. Puede tardar unos segundos en llegar — no cierres esta ventana.",
                  )}
                </p>
              ) : submitting && !showPushWait ? (
                <p className="sub">
                  {t(
                    "dgiDocuments.modal.sendingStep",
                    "Enviando tu respuesta…",
                  )}
                </p>
              ) : null}
              {showSlowLoadingHint ? (
                <span className="meta">
                  <span className="dot" aria-hidden />
                  {t("dgiDocuments.modal.slowResponseHint")}
                </span>
              ) : null}
            </div>
          ) : (
            <>
              {showIdp ? (
                <div className="fx-onb-choice-grid">
                  <OnboardingChoiceCard
                    icon={
                      <span
                        className="fx-onb-brand-ico"
                        style={{
                          maskImage: 'url("/icons/abitab.svg")',
                          WebkitMaskImage: 'url("/icons/abitab.svg")',
                        }}
                        aria-hidden
                      />
                    }
                    name={t(
                      "dgiDocuments.modal.abitab",
                      "Identidad Digital Abitab",
                    )}
                    desc={t(
                      "onboarding.dgiAccessStep.idpAbitabDesc",
                      "Aprobá desde tu app de Abitab.",
                    )}
                    selected={false}
                    disabled={submitting}
                    onClick={() => void handleSubmitIdp("abitab")}
                  />
                  <OnboardingChoiceCard
                    icon={
                      <span
                        className="fx-onb-brand-ico"
                        style={{
                          maskImage: 'url("/icons/tuid.svg")',
                          WebkitMaskImage: 'url("/icons/tuid.svg")',
                        }}
                        aria-hidden
                      />
                    }
                    name={t("dgiDocuments.modal.tuid", "TuID — Antel")}
                    desc={t(
                      "onboarding.dgiAccessStep.idpTuidDesc",
                      "Aprobá desde tu app de TuID.",
                    )}
                    selected={false}
                    disabled={submitting}
                    onClick={() => void handleSubmitIdp("tuid")}
                  />
                </div>
              ) : null}

              {showAbitab ? (
                <div className="fx-onb-form">
                  <div className="fx-onb-field">
                    <label>
                      {t("dgiDocuments.modal.abitabDocType", "Tipo de documento")}
                    </label>
                    <div className="fx-onb-segmented" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={abitabDocType === "cedula"}
                        className={cn(abitabDocType === "cedula" && "active")}
                        onClick={() => setAbitabDocType("cedula")}
                        disabled={submitting}
                      >
                        {t("dgiDocuments.modal.cedula", "Cédula")}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={abitabDocType === "pasaporte"}
                        className={cn(abitabDocType === "pasaporte" && "active")}
                        onClick={() => setAbitabDocType("pasaporte")}
                        disabled={submitting}
                      >
                        {t("dgiDocuments.modal.pasaporte", "Pasaporte")}
                      </button>
                    </div>
                  </div>
                  <div className="fx-onb-field">
                    <label htmlFor="abitab-doc">
                      {t(
                        "dgiDocuments.modal.documentNumber",
                        "Número de documento",
                      )}
                    </label>
                    <input
                      id="abitab-doc"
                      type="text"
                      value={abitabDocNumber}
                      onChange={(e) => {
                        const raw = e.target.value;
                        // For Cédula, strip everything that isn't a digit so the
                        // value DGI receives is clean (no dots, no hyphens).
                        const next =
                          abitabDocType === "cedula"
                            ? raw.replace(/\D/g, "")
                            : raw;
                        setAbitabDocNumber(next);
                      }}
                      placeholder={
                        abitabDocType === "cedula" ? "12345678" : "ABC123456"
                      }
                      inputMode={
                        abitabDocType === "cedula" ? "numeric" : "text"
                      }
                      autoComplete="off"
                      disabled={submitting}
                    />
                  </div>
                </div>
              ) : null}

              {showTuidMethod ? (
                <div className="fx-onb-form">
                  <p className="fx-onb-modal-lead">
                    {t(
                      "dgiDocuments.modal.tuidMethodHint",
                      "Confirmá el ingreso desde tu app TuID.",
                    )}
                  </p>
                </div>
              ) : null}

              {showTuidDoc ? (
                <div className="fx-onb-form">
                  <div className="fx-onb-field">
                    <label htmlFor="tuid-doc">
                      {t(
                        "dgiDocuments.modal.tuidDocument",
                        "Número de documento",
                      )}
                    </label>
                    <input
                      id="tuid-doc"
                      type="text"
                      value={tuidDocument}
                      onChange={(e) => setTuidDocument(e.target.value)}
                      disabled={submitting}
                    />
                  </div>
                </div>
              ) : null}

              {phase === "failed" ? (
                <div
                  className="fx-onb-modal-lead"
                  style={{
                    background: "var(--fx-s-raised-hover)",
                    border: "1px solid var(--fx-warn)",
                    borderRadius: 10,
                    padding: "12px 14px",
                    color: "var(--fx-warn)",
                  }}
                >
                  {failureIsCancelled && !(lastError ?? "").trim()
                    ? t("dgiDocuments.modal.cancelled")
                    : translateDgiConsultaLastError(lastError, t)}
                </div>
              ) : null}
            </>
          )}

          <div
            className="fx-onb-modal-actions"
            style={{ justifyContent: "space-between", marginTop: "auto" }}
          >
            {phase === "failed" ? (
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-secondary"
                onClick={handleClose}
              >
                {t(
                  "onboarding.dgiAccessStep.cancelToManual",
                  "Pasar a manual",
                )}
              </button>
            ) : (
              <div />
            )}
            {showAbitab ? (
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={submitting || !abitabDocNumber.trim()}
                aria-busy={submitting}
                onClick={() => void handleSubmitAbitab()}
              >
                {submitting ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    {t("dgiDocuments.modal.continue", "Continuar")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            ) : null}
            {showTuidMethod ? (
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={submitting}
                aria-busy={submitting}
                onClick={() => void handleSubmitTuidApp()}
              >
                {submitting ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    {t("dgiDocuments.modal.tuidConfirmApp", "Confirmar")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            ) : null}
            {showTuidDoc ? (
              <button
                type="button"
                className="fx-onb-btn fx-onb-btn-accent"
                disabled={submitting || !tuidDocument.trim()}
                aria-busy={submitting}
                onClick={() => void handleSubmitTuidDoc()}
              >
                {submitting ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <>
                    {t("dgiDocuments.modal.continue", "Continuar")}
                    <ArrowRight aria-hidden />
                  </>
                )}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
